import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './BadgeUnlockModal.css';
import { X, Share2, Loader } from 'lucide-react';
import { useAppContext } from '../../AppContext';
import { useTranslation } from 'react-i18next';

const BadgeUnlockModal = ({ badge, isOpen, onClose }) => {
  const { t } = useTranslation();
  const { appFunctions, userId, setMessage } = useAppContext();
  const [isSharing, setIsSharing] = useState(false);

  if (!isOpen || !badge) {
    return null;
  }

  const handleShare = async () => {
    if (!navigator.share) {
      setMessage(t('badgeUnlock.noWebShare'));
      return;
    }

    setIsSharing(true);
    try {
      const generateImage = appFunctions('generateBadgeShareImage');
      const result = await generateImage({ userId, badgeId: badge.id });
      
      await navigator.share({
        title: t('badgeUnlock.shareTitle', { badgeName: badge.name }),
        text: t('badgeUnlock.shareText'),
        url: result.data.shareUrl,
      });

    } catch (error) {
      console.error("Error sharing badge:", error);
      setMessage(`${t('badgeUnlock.shareFailed')}: ${error.message}`);
    } finally {
      setIsSharing(false);
    }
  };

  const modalVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const contentVariants = {
    hidden: { scale: 0.5, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        delay: 0.2,
        type: 'spring', 
        stiffness: 120,
        damping: 15
      } 
    },
    exit: {
        scale: 0.5,
        opacity: 0,
        transition: {
            duration: 0.2
        }
    }
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        delay: 0.5,
        type: 'spring',
        stiffness: 100,
        damping: 10,
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="badge-unlock-overlay"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <motion.div
            className="badge-unlock-content"
            variants={contentVariants}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="close-button">
              <X size={24} />
            </button>
            
            <h2 className="badge-unlock-title">{t('badgeUnlock.title')}</h2>
            
            <motion.div className="badge-icon-container" variants={iconVariants}>
              <img src={badge.iconUrl} alt={badge.name} className="badge-icon" />
              <motion.div className="sparkles"></motion.div>
            </motion.div>

            <h3 className="badge-name">{badge.name}</h3>
            <p className="badge-description">{badge.description}</p>

            <div className="badge-actions">
              <button className="share-button" onClick={handleShare} disabled={isSharing}>
                {isSharing ? (
                  <Loader size={20} className="animate-spin" />
                ) : (
                  <Share2 size={20} />
                )}
                <span>{isSharing ? t('badgeUnlock.generating') : t('badgeUnlock.share')}</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BadgeUnlockModal;
